import { AlertCircle, Check } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
  status: string;
  current: number;
};

export function GedcomImportToast({ status, current }: Props) {
  const { t } = useTranslation();

  const isSuccess = status === 'complete';

  return (
    <div
      className={[
        'relative w-full rounded-sm overflow-hidden border px-4 py-2 shadow-toast backdrop-blur-xl',
        'transition-all duration-300 hover:-translate-y-1 hover:shadow-toast-hover',
        isSuccess
          ? 'border-green-100 shadow-green-100/60 bg-[#f4fde7]'
          : 'border-red-100 shadow-red-100/60 bg-[#fff0f0]'
      ].join(' ')}>
      {/* Left Accent */}
      <div
        className={[
          'absolute left-0 top-0 h-full w-1.5 rounded-l-full',
          isSuccess
            ? 'bg-gradient-to-b from-green-400 via-green-500 to-emerald-500'
            : 'bg-gradient-to-b from-red-400 via-red-500 to-rose-500'
        ].join(' ')}
      />

      {/* Soft Glow */}
      <div
        className={[
          'pointer-events-none absolute -left-16 -top-16 h-44 w-44 rounded-full blur-3xl',
          isSuccess ? 'bg-green-300/25' : 'bg-red-300/25'
        ].join(' ')}
      />

      <div className="flex items-start gap-4 pr-4">
        <AnimatedIcon type={status} />

        <div className="min-w-0 flex-1">
          <p className="text-sm font-bold tracking-tight text-slate-950">
            {isSuccess
              ? t('gedcomMapping.importSuccess.title')
              : t('gedcomMapping.importError.title')}
          </p>

          <p className="mt-1 text-xs leading-relaxed text-slate-800">
            {isSuccess
              ? t('gedcomMapping.importSuccess.message')
              : t('gedcomMapping.importError.message')}
          </p>

          {current && <AnimatedProgress type={status} />}
        </div>
      </div>
    </div>
  );
}

function AnimatedIcon({ type }: { type: string }) {
  const isSuccess = type === 'complete';

  return (
    <div className="relative mt-1 flex h-16 w-16 shrink-0 items-center justify-center">
      {/* Pulse Rings */}
      <span
        className={[
          'absolute h-16 w-16 rounded-full animate-toast-pulse',
          isSuccess ? 'bg-green-400/15' : 'bg-red-400/15'
        ].join(' ')}
      />
      <span
        className={[
          'absolute h-12 w-12 rounded-full animate-toast-pulse-delayed',
          isSuccess ? 'bg-green-400/20' : 'bg-red-400/20'
        ].join(' ')}
      />

      {/* Small Orbit Dots */}
      <span
        className={[
          'absolute left-2 top-3 h-1.5 w-1.5 rounded-full animate-float-dot',
          isSuccess ? 'bg-green-300' : 'bg-red-300'
        ].join(' ')}
      />
      <span
        className={[
          'absolute bottom-2 right-2 h-1.5 w-1.5 rounded-full animate-float-dot-delayed',
          isSuccess ? 'bg-green-300' : 'bg-red-300'
        ].join(' ')}
      />

      {/* Main Icon */}
      <div
        className={[
          'relative z-10 flex h-10 w-10 items-center justify-center rounded-full shadow-lg',
          isSuccess
            ? 'bg-gradient-to-br from-green-400 to-green-600 shadow-green-300/60'
            : 'bg-gradient-to-br from-red-400 to-red-600 shadow-red-300/60'
        ].join(' ')}>
        {isSuccess ? (
          <Check className="text-white" size={24} strokeWidth={3} />
        ) : (
          <AlertCircle className="text-white" size={24} strokeWidth={2.8} />
        )}
      </div>
    </div>
  );
}

function AnimatedProgress({ type }: { type: string }) {
  const isSuccess = type === 'complete';

  return (
    <div className="mt-8 flex items-center gap-3">
      <div
        className={[
          'relative h-1.5 w-56 overflow-hidden rounded-full',
          isSuccess ? 'bg-green-100' : 'bg-red-100'
        ].join(' ')}>
        <div
          className={[
            'absolute left-0 top-0 h-full rounded-full',
            isSuccess
              ? 'w-[78%] bg-gradient-to-r from-green-400 to-green-500'
              : 'w-[32%] bg-gradient-to-r from-red-400 to-red-500'
          ].join(' ')}
        />

        <div
          className={[
            'absolute top-1/2 h-4 w-4 -translate-y-1/2 rounded-full blur-[2px] animate-progress-dot',
            isSuccess ? 'left-[76%] bg-green-400' : 'left-[30%] bg-red-400'
          ].join(' ')}
        />
      </div>

      <div className="flex gap-2">
        {[0, 1, 2, 3, 4].map(item => (
          <span
            key={item}
            className={[
              'h-1.5 w-3 rounded-full opacity-40 animate-trailing-dot',
              isSuccess ? 'bg-green-300' : 'bg-red-300'
            ].join(' ')}
            style={{ animationDelay: `${item * 130}ms` }}
          />
        ))}
      </div>
    </div>
  );
}
